// A catalog field whose text is localized (its value is a framework
// localization key, one entry per language) and whose content is HTML: the
// editor reads and writes the language the dialog is switched to, and the text
// itself is written in the framework's HTML editor — the one the mail
// templates and the CMS `HtmlEditor` fields use.
import React, { useState } from "react"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import HtmlEditor from "aldoo-ra/HtmlEditor"
import { LanguageTag } from "aldoo-ra/Admin/Store/Products/EditingLanguage"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

// `helpKey`: the help center's words on the field, as a "?" beside its label
export default function LocalizedHtmlField({ label, field, editor, disabled = false, title, helpKey = null }) {
  const t = useProductEditorTexts()
  const { read, set, language } = editor
  const [open, setOpen] = useState(false)
  const value = read(field) ?? ""

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-4">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-admin_text_secondary">
          {label}
          <LanguageTag language={language} />
          {helpKey && <HelpHint helpKey={helpKey} />}
        </span>
        <Button
          variant="admin_secondary"
          className="h-[30px] text-xs px-3"
          disabled={disabled}
          onClick={() => setOpen(true)}
          data-testid={`html-editor-${field}`}
        >
          {t`Edit`}{value ? " ✓" : ""}
        </Button>
      </div>
      {/* the text of the language the dialog is on, as plain text: what it
          says matters here, how it is marked up is the editor's business */}
      <p
        className="m-0 max-h-[72px] overflow-hidden text-[13px] leading-5 text-admin_text_secondary"
        data-testid={`preview-${field}`}
      >
        {value ? String(value).replace(/<[^>]+>/g, " ").trim() : t("No %s in %s", label.toLowerCase(), language)}
      </p>

      {open && (
        <Modal
          isOpen
          onClose={() => setOpen(false)}
          closeOnEscape
          variant="admin_fullScreen"
          size="fullscreen"
          styleOverrides={{
            container: "bg-admin_canvas w-full h-full overflow-y-auto",
            contentWrapper: "overflow-y-auto h-[calc(100vh-80px)] scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_canvas",
          }}
        >
          <HtmlEditor
            readOnly={disabled}
            title={`${title || label} [${language}]`}
            content={value || ""}
            save={(content) => {
              set(field, content)
              setOpen(false)
            }}
          />
        </Modal>
      )}
    </div>
  )
}
